import { ReactNode, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Badge, Banner, Button, DataTable, EmptyState, FilterBar, PageHeader, Segmented, Stack, t, toast,
} from '@kanbios/ui';
import { useEnvironmentFilter } from './EnvironmentPicker';
import type { DataTableColumn } from '@kanbios/ui';
import { Shell } from './Shell';
import { useDisplayScope } from './ScopePicker';
import { serverStateColumn } from './ServerState';
import { RefusalPage } from './Refusal';
import { api, unwrap } from '../lib/api';
import { clockTime } from '../lib/format';
import { type Enums, useSession } from '../lib/session';
import { useFillHeight } from '../lib/useFillHeight';

export type ListId = 'applications' | 'projects' | 'clients';

interface ListPage<T> {
  items: T[];
  total: number;
  outOf: number;
  facets: Record<string, Record<string, number>>;
  views: Record<string, number>;
  labels: Record<string, string>;
  generatedAt: string;
  stale: boolean;
}

export type IndexColumn<T> = DataTableColumn<T>;

/** D53: the state column of the three lists — first, a dot only, the legend (with « Pas en production ») in its header. */
export function stateColumn<T extends { state: string | null }>(enums: Enums): IndexColumn<T> {
  return serverStateColumn<T>(enums, row => ({ state: row.state }), { noProduction: true });
}

/** D54: the states whose empty view is good news (« Aucune application en Incident »). */
const TROUBLE_VIEWS = ['incident', 'to_update', 'maintenance', 'unknown'];

/** D54: the EmptyState of a state view (or « Pas en production ») with nothing in it, and the way back to « Tout ». */
export function ViewEmpty({ list, view, icon, onSeeAll }: { list: ListId; view: string; icon: string; onSeeAll: () => void }) {
  const { enums } = useSession();
  const label = enums.listViews[list].find(v => v.value === view)?.label ?? view;
  return (
    <EmptyState framed={false} icon={TROUBLE_VIEWS.includes(view) ? 'check_circle' : icon} title={t(`${list}.empty.view`, { view: label })}
      action={<Button variant="secondary" onClick={onSeeAll}>{t(`${list}.empty.seeAll`)}</Button>} />
  );
}

async function fetchList<T>(id: ListId, query: Record<string, unknown>): Promise<ListPage<T>> {
  const params = { params: { query } };
  // One generated route per list (SPEC-005), same answer shape.
  if (id === 'applications') return unwrap(await api.maintenance.GET('/applications', params as never)) as unknown as ListPage<T>;
  if (id === 'projects') return unwrap(await api.maintenance.GET('/projects', params as never)) as unknown as ListPage<T>;
  return unwrap(await api.maintenance.GET('/clients', params as never)) as unknown as ListPage<T>;
}

/**
 * The common template of the Applications, Projects and Clients lists
 * (SPEC-005, after SPEC-003), DSM index page: PageHeader with the total and
 * the primary action, views with their counts, one DataTable that owns the
 * FilterBar, the legend, the export and the pagination (server side).
 */
export function IndexList<T extends { name: string }>({
  id, title, subtitle, actions, columns, facets, rowHref, emptyFor, search: searchPlaceholder, tabs,
}: {
  id: ListId;
  title: string;
  subtitle: (data: ListPage<T> | undefined) => string;
  actions?: ReactNode;
  columns: IndexColumn<T>[];
  facets: { key: string; label: string; valueLabel?: (value: string) => string }[];
  rowHref: (row: T) => string;
  /** EmptyState of a view with no row (« Aucun projet n'a de sujet ouvert… »). */
  emptyFor: (view: string, setView: (v: string) => void, data: ListPage<T> | undefined) => ReactNode;
  search: string;
  /** Section tabs above the list (Clients › Domaines et DNS, SPEC-029). */
  tabs?: ReactNode;
}) {
  const fill = useFillHeight();
  const { enums, can } = useSession();
  const navigate = useNavigate();
  const views = enums.listViews[id];
  const [view, setView] = useState<string>(views[0]!.value);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState('state:asc');
  const scope = useDisplayScope();
  // D73: the global environment filter, production winning.
  const env = useEnvironmentFilter();

  const filterParam = Object.fromEntries(Object.entries(filters).filter(([, v]) => v.length).map(([k, v]) => [k, v.join(',')]));
  const list = useQuery({
    queryKey: [id, scope, env, view, search, filterParam, page, pageSize, sort],
    queryFn: () => fetchList<T>(id, { scope, env, view, q: search || undefined, page, pageSize, sort, filter: filterParam }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

  const stateLabel = (value: string) => (value === 'none' ? t('inventory.noProd') : enums.states.find(s => s.value === value)?.label ?? value);
  const labelOf = (facet: string, value: string) => {
    const def = facets.find(f => f.key === facet);
    if (def?.valueLabel) return def.valueLabel(value);
    if (facet === 'state') return stateLabel(value);
    return list.data?.labels[value] ?? value;
  };
  const filterDefs = facets.map(f => ({
    key: f.key,
    label: f.label,
    type: 'multi' as const,
    options: Object.entries(list.data?.facets[f.key] ?? {}).sort(([a], [b]) => labelOf(f.key, a).localeCompare(labelOf(f.key, b), 'fr'))
      .map(([value, count]) => ({ value, label: `${labelOf(f.key, value)} (${count})` })),
  }));

  if (list.error) return <Shell crumbs={[{ label: title }]}><RefusalPage error={list.error} /></Shell>;

  const total = list.data?.total ?? 0;
  const exportCsv = () => {
    const params = new URLSearchParams({ scope, env, view, format: 'csv', sort });
    if (search) params.set('q', search);
    for (const [key, value] of Object.entries(filterParam)) params.set(`filter[${key}]`, value);
    window.location.assign(`/api/maintenance/${id}/export?${params.toString()}`);
    toast({ title: t(`${id}.exported`, { n: total }), tone: 'info' });
  };
  const changeView = (v: string) => { setView(v); setPage(1); };
  const hasFilters = Boolean(search) || Object.keys(filterParam).length > 0;

  return (
    <Shell crumbs={[{ label: title }]}>
      <Stack gap={24}>
        <PageHeader
          title={title}
          status={list.data ? <Badge tone="neutral">{list.data.outOf}</Badge> : undefined}
          subtitle={subtitle(list.data)}
          actions={actions}
        />
        {tabs}
        {list.data?.stale && (
          <Banner tone="warn" title={t('servers.stale.title')}>{t('servers.stale.body', { when: clockTime(list.data.generatedAt) })}</Banner>
        )}
        <Segmented
          label={t('servers.views')}
          size="sm"
          value={view}
          onChange={changeView}
          items={views.map(v => ({ id: v.value, label: v.label, count: list.data?.views[v.value] }))}
        />
        <div ref={fill[0]}>
          <DataTable
            height={fill[1]}
            label={t(`${id}.table`)}
            columns={columns}
            rows={list.data?.items ?? []}
            rowKey="name"
            manual
            totalCount={total}
            toolbarStart={
              <FilterBar
                label={t(`${id}.filters`)}
                filters={filterDefs}
                value={filters}
                onChange={v => { setFilters(v as Record<string, string[]>); setPage(1); }}
                search={search}
                onSearchChange={q => { setSearch(q); setPage(1); }}
                searchPlaceholder={searchPlaceholder}
              />
            }
            toolbarSearch={false}
            toolbarFilters={false}
            density="compact"
            loading={list.isPending}
            defaultSort={{ key: 'state', dir: 'asc' }}
            onQueryChange={q => { if (q.sort) { setSort(`${q.sort.key}:${q.sort.dir}`); setPage(1); } }}
            onRowClick={(row: T) => navigate(rowHref(row))}
            toolbarActions={can('server.export') ? <Button variant="secondary" size="sm" icon="download" onClick={exportCsv} disabled={total === 0}>{t(`${id}.export`, { n: total })}</Button> : undefined}
            empty={hasFilters
              ? <EmptyState framed={false} icon="filter_alt_off" title={t('inventory.noResult')} />
              : emptyFor(view, changeView, list.data)}
            pagination={{ page, pageSize, total, onPageChange: setPage, pageSizes: [25, 50, 100], onPageSizeChange: size => { setPageSize(size); setPage(1); } }}
          />
        </div>
      </Stack>
    </Shell>
  );
}
